import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import UploadIcon from '@mui/icons-material/CloudUpload';
import NotificationAdminService from '../services/notificationAdminService';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const emptyForm = {
  useCaseCode: '',
  enabled: true,
  titleEn: '',
  titleEs: '',
  bodyEn: '',
  bodyEs: '',
  imageUrl: '',
  actionUrl: '',
  category: 'SYSTEM',
  priority: 'MEDIUM',
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ display: 'block', color: 'text.secondary', letterSpacing: 1, mb: 1.5, mt: 0.5 }}
  >
    {children}
  </Typography>
);

const NotificationTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('edit'); // 'create' | 'edit'
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await NotificationAdminService.listTemplates();
      setTemplates(res?.data || []);
    } catch (e) {
      notify(e?.message || 'Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const res = await NotificationAdminService.listEnabledCategories();
      setCategories(res?.data || []);
    } catch (e) {
      notify('Failed to load categories', 'error');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadCategories();
  }, [loadTemplates, loadCategories]);

  const openCreate = () => {
    setForm({ ...emptyForm, useCaseCode: '' });
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEdit = (template) => {
    setForm({
      useCaseCode: template.useCaseCode,
      enabled: template.enabled ?? false,
      titleEn: template.titleEn || '',
      titleEs: template.titleEs || '',
      bodyEn: template.bodyEn || '',
      bodyEs: template.bodyEs || '',
      imageUrl: template.imageUrl || '',
      actionUrl: template.actionUrl || '',
      category: template.category || 'SYSTEM',
      priority: template.priority || 'MEDIUM',
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await NotificationAdminService.uploadImage(file);
      if (url) {
        setForm((f) => ({ ...f, imageUrl: url }));
        notify('Image uploaded');
      }
    } catch (err) {
      notify(err?.message || 'Image upload failed', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.titleEn.trim() || !form.bodyEn.trim()) {
      notify('English title and body are required', 'error');
      return;
    }

    if (dialogMode === 'create' && !form.useCaseCode.trim()) {
      notify('Use case code is required', 'error');
      return;
    }

    setSaving(true);
    try {
      await NotificationAdminService.upsertTemplate(form.useCaseCode, form);
      notify('Template saved');
      setDialogOpen(false);
      loadTemplates();
    } catch (e) {
      notify(e?.message || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (template, enabled) => {
    try {
      await NotificationAdminService.toggleTemplate(template.useCaseCode, enabled);
      notify(`Template ${enabled ? 'enabled' : 'disabled'}`);
      loadTemplates();
    } catch (e) {
      notify(e?.message || 'Failed to toggle template', 'error');
    }
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActiveIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Notification Templates</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the content and on/off switch for each backend notification use case.
              If a template is disabled, that notification will not be sent.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Template
        </Button>
      </Box>

      {/* Templates table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Use Case</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Title (EN)</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Action Link</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No notification use cases found.
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((t) => (
                  <TableRow key={t.useCaseCode} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                        {t.useCaseCode}
                      </Typography>
                      {!t.configured && (
                        <Typography variant="caption" color="warning.main">Not configured</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.isCustom ? 'Custom' : 'Enum'}
                        color={t.isCustom ? 'primary' : 'default'}
                      />
                      {t.isCustom && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Add to enum
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.enabled ? 'Enabled' : 'Disabled'}
                        color={t.enabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" noWrap>{t.titleEn || '-'}</Typography>
                    </TableCell>
                    <TableCell>{t.category || '-'}</TableCell>
                    <TableCell>{t.actionUrl || <em>default (notifications page)</em>}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t.configured ? (t.enabled ? 'Disable' : 'Enable') : 'Configure first'}>
                        <span>
                          <Switch
                            size="small"
                            checked={!!t.enabled}
                            disabled={!t.configured}
                            onChange={(e) => handleToggle(t, e.target.checked)}
                          />
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit template">
                        <IconButton size="small" onClick={() => openEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Template form dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Notification Template' : `Edit Template — ${form.useCaseCode}`}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* Content section */}
          <SectionLabel>Content</SectionLabel>
          <Stack spacing={2.5}>
            
            {/* Use Case Code (only for create) */}
            {dialogMode === 'create' && (
              <TextField
                label="Use Case Code"
                fullWidth
                required
                value={form.useCaseCode}
                onChange={handleField('useCaseCode')}
                helperText="Uppercase letters, numbers, and underscores (e.g., ORDER_COMPLETED). Add this to NotificationUseCase enum after saving."
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            )}

            {/* Enabled toggle */}
            <FormControlLabel
              control={
                <Switch 
                  checked={form.enabled} 
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
              }
              label={form.enabled ? 'Enabled (notifications will be sent)' : 'Disabled (notifications are suppressed)'}
            />

            {/* Title EN */}
            <TextField
              label="Title (English)"
              fullWidth
              required
              value={form.titleEn}
              onChange={handleField('titleEn')}
            />

            {/* Title ES */}
            <TextField
              label="Title (Spanish)"
              fullWidth
              value={form.titleEs}
              onChange={handleField('titleEs')}
            />

            {/* Body EN */}
            <TextField
              label="Body (English)"
              fullWidth
              required
              multiline
              minRows={3}
              value={form.bodyEn}
              onChange={handleField('bodyEn')}
              helperText="Use {{placeholder}} tokens for dynamic values."
            />

            {/* Body ES */}
            <TextField
              label="Body (Spanish)"
              fullWidth
              multiline
              minRows={3}
              value={form.bodyEs}
              onChange={handleField('bodyEs')}
              helperText="Use {{placeholder}} tokens for dynamic values."
            />

            {/* Image URL + upload button */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                label="Image URL"
                fullWidth
                value={form.imageUrl}
                onChange={handleField('imageUrl')}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={uploading}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, height: 56 }}
              >
                {uploading ? 'Uploading…' : 'Upload'}
                <input hidden type="file" accept="image/*" onChange={handleUpload} />
              </Button>
            </Box>

            {/* Action URL */}
            <TextField
              label="Action link (deep link)"
              fullWidth
              value={form.actionUrl}
              onChange={handleField('actionUrl')}
              helperText="Where the user lands on click, e.g. /verifications. Leave blank to default to the notifications page."
            />
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Settings section */}
          <SectionLabel>Settings</SectionLabel>
          <Stack spacing={2.5}>
            
            {/* Category */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select 
                label="Category" 
                value={form.category} 
                onChange={handleField('category')}
                disabled={categoriesLoading}
              >
                {categories.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select label="Priority" value={form.priority} onChange={handleField('priority')}>
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationTemplatesPage;
